import { IconButton } from '@material-ui/core';
import { CustomTheme } from '@nemuru-eng/js-api-client';
import { Insurance, InsuranceProvider } from '@nemuru-eng/js-api-client/slim';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { CheckoutSessionDetailActionsDialog } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/CheckoutSessionDetailActionsDialog';
import { CheckoutSessionHpp } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/entities/CheckoutSessionHpp';
import { useDownloadQuadisLifeInsuranceOffer } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/hooks/useDownloadQuadisLifeInsuranceOffer';
import { CheckoutSessionDetailAction } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/types/CheckoutSessionDetailAction';
import { CreditCardPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/_shared/entities/PaymentMethodDetail/CreditCardPaymentMethodDetailEntity';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { StandardButton, StandardButtonProps } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { isIbcStandsAgentUser, isIbcStandsSupervisorUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface CheckoutSessionActionsProps extends StandardButtonProps {}

const ACTIONS_REQUIRING_REFRESH = [
  CheckoutSessionDetailAction.EDIT_CART,
  CheckoutSessionDetailAction.EDIT_DOWN_PAYMENT,
  CheckoutSessionDetailAction.SHARE,
  CheckoutSessionDetailAction.CANCEL_CHECKOUT_SESSION,
];

export const CheckoutSessionActions = (props: CheckoutSessionActionsProps) => {
  const { checkoutSessionEntity, paymentMethodDetailEntities } = useCheckoutSessionContext();
  const { refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSession();
  const { userEntity } = useUserContext();

  const { sm } = useScreen();
  const { palette } = useCustomTheme();
  const { opened, setOpened, toggleDialog } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();

  const [alreadyShared, setAlreadyShared] = useState(false);
  const [currentAction, setCurrentAction] = useState<CheckoutSessionDetailAction | undefined>();

  // The Quadis insurance offer belongs to the loan: only Quadis agents get one, and it only makes
  // sense where the loan is priced with WeeCover insurance, which is what the offer quotes.
  const quadisLoanId = useMemo(
    () =>
      paymentMethodDetailEntities.find((paymentMethodDetailEntity) => {
        if (
          !(paymentMethodDetailEntity instanceof AbstractLoanPaymentMethodDetailEntity) ||
          paymentMethodDetailEntity.agent.customTheme !== CustomTheme.QUADIS ||
          !paymentMethodDetailEntity.paymentMethodId ||
          !paymentMethodDetailEntity.loan
        ) {
          return false;
        }

        const insurance = paymentMethodDetailEntity.loan.pricingInsurance as Insurance | null;

        return insurance?.provider === InsuranceProvider.WEE_COVER;
      })?.paymentMethodId,
    [paymentMethodDetailEntities],
  );

  const { downloadQuadisLifeInsuranceOffer, loading: downloadingQuadisLifeInsuranceOffer } =
    useDownloadQuadisLifeInsuranceOffer(quadisLoanId);

  const isStandsAgent = !!userEntity && isIbcStandsAgentUser(userEntity);
  const isStandsSupervisor = !!userEntity && isIbcStandsSupervisorUser(userEntity);

  // Stands applications can only be shared once scoring is done: before that the client could change the data that is
  // scored (e.g. use somebody else's), and the presential KYC done by the agent would no longer apply.
  const isShareable = useMemo(() => {
    if (!checkoutSessionEntity?.isShareable()) return false;
    if (!isStandsAgent && !isStandsSupervisor) return true;

    const creditCardPaymentMethodDetailEntity = paymentMethodDetailEntities.find(
      (paymentMethodDetailEntity) => paymentMethodDetailEntity instanceof CreditCardPaymentMethodDetailEntity,
    ) as CreditCardPaymentMethodDetailEntity | undefined;

    return !!creditCardPaymentMethodDetailEntity?.progress?.hasPassedScoring;
  }, [checkoutSessionEntity, paymentMethodDetailEntities, isStandsAgent, isStandsSupervisor]);

  const hasDistributions = useMemo(
    () => !!checkoutSessionEntity && new CheckoutSessionHpp(checkoutSessionEntity.checkoutSession).hasBeenDistributed,
    [checkoutSessionEntity],
  );

  useEffect(() => {
    // Open *once* share dialog if share param is present
    // and checkout session is shareable
    const shareParam = !!searchParams.get('share');
    if (shareParam && !alreadyShared && isShareable) {
      setAlreadyShared(true);
      setCurrentAction(CheckoutSessionDetailAction.SHARE);
      searchParams.delete('share');
      setSearchParams(searchParams);
      setOpened(true);
    }
  }, [alreadyShared, isShareable, searchParams]);

  useEffect(() => {
    if (opened === false && currentAction && ACTIONS_REQUIRING_REFRESH.includes(currentAction)) {
      refreshCheckoutSessionAndPaymentMethodEntities();
    }
  }, [opened, currentAction]);

  if (!checkoutSessionEntity) {
    return null;
  }

  const toggleActionDialog = (actionType: CheckoutSessionDetailAction) => {
    setCurrentAction(actionType);
    toggleDialog();
  };

  const actions: ActionsOption[] = [
    {
      display: <Text tId={'SEE_DISTRIBUTIONS'} />,
      onClick: () => toggleActionDialog(CheckoutSessionDetailAction.SEE_DISTRIBUTIONS),
      iconName: 'done_all',
      disabled: !hasDistributions,
      divider: true,
    },
    {
      display: <Text tId={'SHARE_WITH_CUSTOMER'} />,
      onClick: () => toggleActionDialog(CheckoutSessionDetailAction.SHARE),
      disabled: !isShareable,
      iconName: 'ios_share',
    },
    ...(quadisLoanId
      ? [
          {
            display: <Text tId={'DOWNLOAD_INSURANCE_OFFER'} />,
            onClick: downloadQuadisLifeInsuranceOffer,
            iconName: 'download',
            loading: downloadingQuadisLifeInsuranceOffer,
          },
        ]
      : []),
    ...(isStandsAgent
      ? []
      : [
          {
            display: <Text tId={'CANCEL'} />,
            onClick: () => toggleActionDialog(CheckoutSessionDetailAction.CANCEL_CHECKOUT_SESSION),
            iconName: 'cancel',
            disabled: checkoutSessionEntity.hasFinalStatus(),
            color: 'error' as const,
          },
        ]),
  ];

  return (
    <>
      {sm && isShareable && (
        <StandardButton
          {...props}
          variant="outlined"
          size="small"
          endIcon={
            <Icon size={16} color={palette.text.secondary}>
              ios_share
            </Icon>
          }
          onClick={() => toggleActionDialog(CheckoutSessionDetailAction.SHARE)}
        >
          <Font variant={'subtitle2'} color={'textSecondary'}>
            <Text strong tId={'SHARE_WITH_CUSTOMER'} />
          </Font>
        </StandardButton>
      )}
      <IconButton {...props} size="small" style={{ marginLeft: 0 }}>
        <MoreActionsMenu actionsOptionsArray={actions}>
          <Font variant={'subtitle2'} color={'textSecondary'}>
            <strong>
              <i className={'material-icons-outlined'} style={{ verticalAlign: '-5px', fontSize: '1.5em' }}>
                more_horiz
              </i>
            </strong>
          </Font>
        </MoreActionsMenu>
      </IconButton>
      {currentAction && (
        <CheckoutSessionDetailActionsDialog
          currentAction={currentAction}
          checkoutSession={checkoutSessionEntity.checkoutSession}
          useDialogProps={{ opened, setOpened, toggleDialog }}
          hppId={checkoutSessionEntity.hppId}
        />
      )}
    </>
  );
};
